import { CartAddEvent } from '@theme/events';

// Observe completed native adds only. Cart edits, restores and failed adds must
// never announce success. Prices and checkout remain on Orbit's full cart page.
class TherasageCartConfirmation extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.dialog = this.querySelector('dialog');
    this.items = this.querySelector('[data-confirmation-items]');
    this.cartLink = this.querySelector('[data-confirmation-cart]');
    this.triggers = new WeakMap();
    this.origins = new WeakMap();

    document.addEventListener('submit', (event) => {
      const component = event.target.closest('product-form-component');
      if (component) {
        const submitted = event.submitter;
        const trigger = submitted?.getClientRects().length ? submitted : document.activeElement;
        this.triggers.set(component, trigger);
        const rect = trigger?.getBoundingClientRect?.();
        if (rect) this.origins.set(component, { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
      }
    }, { capture: true, signal });

    document.addEventListener(CartAddEvent.eventName, (event) => {
      const data = event.detail?.data;
      if (!(event instanceof CartAddEvent) || data?.didError || data?.source !== 'product-form-component') return;
      const addedItems = data.addedItems?.filter(item => item && (item.variant_id || item.id) && item.quantity > 0);
      if (!addedItems?.length) return;
      const trigger = this.triggers.get(event.target);
      this.returnFocus = trigger instanceof HTMLElement && trigger.getClientRects().length && !trigger.closest('[hidden], [inert], [aria-hidden="true"]')
        ? trigger : document.activeElement;
      this.show(addedItems, this.origins.get(event.target));
    }, { signal });

    this.cartLink.addEventListener('click', () => this.dialog.close(), { signal });

    this.querySelectorAll('[data-confirmation-close]').forEach(button => {
      button.addEventListener('click', () => this.dialog.close(), { signal });
    });
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const controls = [...this.dialog.querySelectorAll('a[href], button:not([disabled])')];
      const first = controls[0];
      const last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }, { signal });
    this.dialog.addEventListener('click', (event) => {
      if (event.target !== this.dialog) return;
      const box = this.dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
    }, { signal });
    this.dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('therasage-cart-confirmation-open');
      if (this.returnFocus instanceof HTMLElement && this.returnFocus.isConnected && !this.returnFocus.closest('[hidden], [inert]')) {
        this.returnFocus.focus({ preventScroll: true });
      }
    }, { signal });
    // A back/forward restoration must not restore an old success modal.
    window.addEventListener('pagehide', () => this.dialog.close(), { signal });
  }

  show(addedItems, origin) {
    const sequence = this.sequence = (this.sequence || 0) + 1;
    const rows = addedItems.map(item => {
      const row = document.createElement('li');
      row.className = 'therasage-cart-confirmation__item';
      const imageUrl = item.featured_image?.url || item.image;
      if (typeof imageUrl === 'string' && imageUrl) {
        const url = new URL(imageUrl, window.location.origin);
        if (url.protocol === 'https:') {
          url.searchParams.set('width', '160');
          const image = document.createElement('img');
          image.className = 'therasage-cart-confirmation__image';
          image.src = url.href;
          image.alt = '';
          image.width = image.height = 80;
          row.append(image);
        }
      }
      const text = document.createElement('div');
      const name = document.createElement('p');
      name.className = 'therasage-cart-confirmation__name';
      name.textContent = item.product_title || item.title || 'Your item';
      const detail = document.createElement('p');
      detail.className = 'therasage-cart-confirmation__detail';
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      detail.textContent = [variant, `Quantity: ${item.quantity}`].filter(Boolean).join(' · ');
      text.append(name, detail);
      row.append(text);
      return row;
    });
    this.items.replaceChildren(...rows);
    // Let the originating quick-add dialog finish its synchronous success handling.
    queueMicrotask(async () => {
      await this.fly(addedItems[0], origin);
      if (!this.isConnected || sequence !== this.sequence) return;
      if (!this.dialog.open) this.dialog.showModal();
      document.documentElement.classList.add('therasage-cart-confirmation-open');
      this.cartLink.focus({ preventScroll: true });
    });
  }

  async fly(item, origin) {
    this.flight?.cancel();
    if (!origin || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const target = [...document.querySelectorAll('.header-actions__cart-icon, cart-icon, [data-cart-icon], a[href$="/cart"]')]
      .find(el => { const r = el.getBoundingClientRect(); return r.width && r.height && r.top >= 0 && r.top < window.innerHeight; });
    if (!target) return;
    const rect = target.getBoundingClientRect();
    const x = Math.max(32, Math.min(window.innerWidth - 32, origin.x));
    const y = Math.max(32, Math.min(window.innerHeight - 32, origin.y));
    const dx = rect.left + rect.width / 2 - x;
    const dy = rect.top + rect.height / 2 - y;
    const bubble = document.createElement('div');
    bubble.className = 'therasage-cart-flight';
    bubble.setAttribute('aria-hidden', 'true');
    bubble.style.left = `${x - 28}px`;
    bubble.style.top = `${y - 28}px`;
    const source = item.featured_image?.url || item.image;
    try {
      const url = source && new URL(source, window.location.origin);
      if (url?.protocol === 'https:') {
        url.searchParams.set('width', '160');
        const image = document.createElement('img');
        image.alt = '';
        image.src = url.href;
        image.addEventListener('error', () => image.remove(), { once: true });
        bubble.append(image);
      }
    } catch { /* Keep the orange fallback when image data is unavailable. */ }
    document.body.append(bubble);
    try {
      this.flight = bubble.animate([
        { transform: 'translate(0, 0) scale(1)', opacity: 1 },
        { transform: `translate(${dx * .35}px, ${dy * .65 - 45}px) scale(.85)`, opacity: 1, offset: .5 },
        { transform: `translate(${dx}px, ${dy}px) scale(.2)`, opacity: 0 }
      ], { duration: 650, easing: 'ease-in-out', fill: 'forwards' });
      await this.flight.finished;
    } catch { /* Confirmation still opens if animation is cancelled or unsupported. */ }
    finally { bubble.remove(); }
  }

  disconnectedCallback() {
    this.flight?.cancel();
    this.dialog?.close();
    document.documentElement.classList.remove('therasage-cart-confirmation-open');
    this.controller?.abort();
    this.controller = null;
  }
}

if (!customElements.get('therasage-cart-confirmation')) {
  customElements.define('therasage-cart-confirmation', TherasageCartConfirmation);
}
